/**
 * EVERY PAGE OF SETTINGS, AND EVERY SETTING ON IT - ONE LIST.
 *
 * Gordon approved the reorganisation on 26 October: a grouped left-hand nav,
 * six groups, a search over every individual setting, addresses of the form
 * /settings/:group/:page#anchor, and every old ?tab= address redirected.
 *
 * The nav, the search, the "On this page" chips, the redirects from the old
 * addresses, Privacy & monitoring and the Overview's labels are all built
 * from this one list, so none of them can drift from the others - the same
 * idea as Workflows' catalogue. The e2e suite (e2e/settings-nav.mjs) reads
 * this very file and walks it: every page opens, every anchor exists, every
 * old address lands where this says.
 *
 * NO IMPORTS AND NO JSX, on purpose: the test imports it as it is. Icons are
 * named here and drawn by icons.js; components are mapped by page id in
 * Settings.js.
 *
 * A PAGE
 *   id, label, icon      the nav entry
 *   lead                 one line under the title: what this page controls
 *   legacy               old addresses that land here: 'tab', 'tab#hash',
 *                        'tab&part=x' (Assets' sub-tabs), with an optional
 *                        '>#anchor' or '>?query' for where on the page
 *   settings             what the search finds on this page:
 *     anchor             the element id is `s-<anchor>`, and the address
 *                        /settings/<group>/<page>#<anchor>
 *     label, keywords    what the search matches (and shows)
 *     key                the app_settings key, for the Overview's "last
 *                        changed" list
 *     value(ctx)         the current value as a few words, from what the
 *                        shell has loaded: ctx.rules (GET /app-settings) and
 *                        ctx.features (GET /features)
 *     privacy            'records' | 'exposes' - listed on Privacy &
 *                        monitoring, with `why`
 *   href                 a page that lives outside Settings (Ticket
 *                        templates stay in Ticket Mgmt): the nav links there
 *   subs                 sub-pages with their own address (Documentation)
 */

/* -------------------------------------------------------------- values */

const onOff = (v) => (v ? 'On' : 'Off');
const rule = (ctx, key) => (ctx && ctx.rules ? ctx.rules[key] : undefined);
const has = (ctx, key) => Boolean(ctx && ctx.rules && key in ctx.rules);

/** A feature switch's audience in its own words: "Everyone", "Off"... */
export function featureWord(ctx, key) {
  const list = (ctx && ctx.features && ctx.features.features) || [];
  const f = list.find((x) => x.key === key);
  if (!f) return null;
  const c = (f.choices || []).find((x) => x.value === f.audience);
  const extra = (f.allow || []).length + (f.except || []).length;
  return `${c ? c.label : f.audience}${extra ? ` (+${extra} exception${extra === 1 ? '' : 's'})` : ''}`;
}

/* -------------------------------------------------------------- groups */

export const GROUPS = [
  {
    id: 'people',
    label: 'People & access',
    pages: [
      {
        id: 'users',
        label: 'Users',
        icon: 'users',
        lead: 'Everybody with an NCMB account: who is an administrator or approver, who also fixes tickets, and who is switched off.',
        legacy: ['users', 'admins>?role=admins'],
        settings: [
          { anchor: 'add', label: 'Add a user before they sign in', keywords: 'new user create invite account email' },
          { anchor: 'access', label: 'Administrators and approvers', keywords: 'admin approver role access rights grant administrator manager director' },
          { anchor: 'users', label: 'Deactivate or reactivate a user', keywords: 'switch off block disable remove leaver deactivated' },
          { anchor: 'users', label: 'Who also fixes tickets (Work)', keywords: 'worker internal contractor invoices charge-out rate' },
          { anchor: 'add', label: 'Line manager (who approves their catalogue requests)', keywords: 'manager reports to boss line request something catalogue approval' }
        ]
      },
      {
        // Joiners and leavers (113): when a checklist starts by itself, and
        // the two checklists. The checklists being worked through are under
        // Advanced -> Joiners & leavers (/people).
        id: 'joiners-leavers',
        label: 'Joiners & leavers',
        icon: 'people-checklist',
        lead: 'The checklists IT works through when somebody joins or leaves, and when one starts by itself.',
        legacy: [],
        settings: [
          { anchor: 'auto', label: 'Start a joiner checklist when somebody is added', keywords: 'joiner new starter onboarding checklist automatic', key: 'people_joiner_auto' },
          { anchor: 'auto', label: 'Start a leaver checklist when somebody is deactivated', keywords: 'leaver offboarding exit checklist automatic deactivate', key: 'people_leaver_auto' },
          { anchor: 'inactive', label: 'Not signed in: after how many days', keywords: 'inactive dormant stale account last login sign-in 30 days', key: 'people_inactive_days' },
          { anchor: 'joiner', label: 'The joiner checklist', keywords: 'joiner new starter onboarding steps template laptop account mfa' },
          { anchor: 'leaver', label: 'The leaver checklist', keywords: 'leaver offboarding exit steps template devices licences access removed' }
        ]
      },
      {
        id: 'departments',
        label: 'Departments',
        icon: 'departments',
        lead: 'The departments people belong to and tickets are raised for.',
        legacy: ['departments'],
        settings: [
          { anchor: 'list', label: 'Departments', keywords: 'department team office unit' }
        ]
      },
      {
        id: 'contractors',
        label: 'Contractors',
        icon: 'contractors',
        lead: 'The outside IT people who fix tickets: their details, rates, and what they may do in their portal.',
        legacy: ['contractors'],
        settings: [
          { anchor: 'form', label: 'Add a contractor', keywords: 'new contractor supplier technician rate billing skills' },
          { anchor: 'list', label: 'May manage ticket templates', keywords: 'contractor portal templates permission edit' },
          { anchor: 'list', label: 'Contractor sign-in', keywords: 'login code portal access email' }
        ]
      },
      {
        id: 'feature-access',
        label: 'Feature access',
        icon: 'features',
        lead: 'Who can use each part of Jarvis - every feature switch on one page. Each also appears on its own area’s page.',
        legacy: ['features'],
        settings: [
          { anchor: 'feature-links', label: 'Links: who can use it', keywords: 'feature switch links shelf', value: (c) => featureWord(c, 'links') },
          { anchor: 'feature-todos', label: 'To do: who can use it', keywords: 'feature switch todo to-do tasks lists', value: (c) => featureWord(c, 'todos') },
          { anchor: 'feature-meetings', label: 'Meetings: who can use it', keywords: 'feature switch meetings', value: (c) => featureWord(c, 'meetings') },
          { anchor: 'feature-recording', label: 'Call recording & transcription: who can use it', keywords: 'feature switch record recording transcript transcription', value: (c) => featureWord(c, 'recording') },
          { anchor: 'feature-ai', label: 'AI summaries & minutes (Claude): who can use it', keywords: 'feature switch ai claude summary minutes', value: (c) => featureWord(c, 'ai') },
          { anchor: 'feature-docs', label: 'Documentation: who can open it', keywords: 'feature switch docs documentation runbooks', value: (c) => featureWord(c, 'docs') },
          { anchor: 'feature-help', label: 'Help: who can read it', keywords: 'feature switch help pages articles try these first staff', value: (c) => featureWord(c, 'help') },
          { anchor: 'feature-licences', label: 'Licences: on for administrators, or off', keywords: 'feature switch licences licenses subscriptions software seats renewals register', value: (c) => featureWord(c, 'licences') },
          { anchor: 'feature-contracts', label: 'Contracts: on for administrators, or off', keywords: 'feature switch contracts contractor agreement rate card cap notice renewal register', value: (c) => featureWord(c, 'contracts') },
          { anchor: 'feature-gdpr', label: 'GDPR tools: on for administrators, or off', keywords: 'feature switch gdpr data protection export erasure retention record of processing', value: (c) => featureWord(c, 'gdpr') },
          { anchor: 'feature-catalogue', label: 'Request something (service catalogue): who can use it', keywords: 'feature switch catalogue request something service items order ask', value: (c) => featureWord(c, 'catalogue') },
          { anchor: 'history', label: 'Recent changes to feature access', keywords: 'history log audit who changed' }
        ]
      },
      {
        id: 'profile-photos',
        label: 'Profile photos',
        icon: 'photo',
        lead: 'Whether people can put a photo of themselves in the circles, and every photo there is.',
        legacy: ['rules#photos'],
        settings: [
          {
            anchor: 'photos', label: 'Profile photos', keywords: 'picture avatar face photo initials', key: 'profile_photos',
            value: (c) => ({ allowed: 'Everybody', staff: 'Staff only', off: 'Off' }[rule(c, 'profile_photos')] || null),
            privacy: 'exposes', why: 'Shows a photo of each person to everybody who can already see them in chat.'
          },
          { anchor: 'photo-moderation', label: 'Photos people have added', keywords: 'take down remove photo moderation' }
        ]
      }
    ]
  },
  {
    id: 'tickets',
    label: 'Tickets & workflow',
    pages: [
      {
        id: 'ticket-rules',
        label: 'Ticket rules',
        icon: 'rules',
        lead: 'How tickets are raised and recorded: approvers, private notes, the approval digest and where a change was made.',
        legacy: ['rules', 'rules#approver>#approver', 'rules#private-notes>#private-notes', 'rules#digest>#digest', 'rules#location>#ask-location'],
        settings: [
          { anchor: 'approver', label: 'Every ticket must name an approver', keywords: 'approval approver required mandatory', key: 'require_approver', value: (c) => onOff(rule(c, 'require_approver') !== false) },
          { anchor: 'private-notes', label: 'Allow private notes on tickets', keywords: 'private note internal comment hidden requester', key: 'ticket_private_notes', value: (c) => onOff(rule(c, 'ticket_private_notes') === true) },
          {
            anchor: 'ask-location', label: 'Ask for people’s location', keywords: 'gps location where geolocation browser', key: 'ask_location',
            value: (c) => onOff(rule(c, 'ask_location') !== false),
            privacy: 'records', why: 'Asks the browser where somebody is when they comment on, change or log hours on a ticket, and keeps the answer with the change.'
          },
          {
            anchor: 'require-location', label: 'A change to a ticket must record where it was made', keywords: 'gps location required refuse block', key: 'require_location',
            value: (c) => onOff(rule(c, 'require_location') === true && rule(c, 'ask_location') !== false),
            privacy: 'records', why: 'Refuses a change to a ticket unless the browser reports a location.'
          },
          { anchor: 'digest', label: 'Approval digest: goes out at', keywords: 'email digest morning hour approvers', key: 'approval_digest_hour', value: (c) => (Number.isFinite(Number(rule(c, 'approval_digest_hour'))) ? `${String(Number(rule(c, 'approval_digest_hour'))).padStart(2, '0')}:00` : null) },
          { anchor: 'digest', label: 'Approval digest: or early, once N are waiting', keywords: 'email digest queue waiting early', key: 'approval_digest_after', value: (c) => (Number(rule(c, 'approval_digest_after')) > 0 ? `${Number(rule(c, 'approval_digest_after'))} waiting` : 'Off') }
        ]
      },
      {
        id: 'categories',
        label: 'Ticket categories',
        icon: 'categories',
        lead: 'What a ticket can be filed under. Custom fields and SLA rules are set per category.',
        legacy: ['categories'],
        settings: [
          { anchor: 'list', label: 'Ticket categories', keywords: 'category type hardware software network' }
        ]
      },
      {
        id: 'priorities-statuses',
        label: 'Priorities & statuses',
        icon: 'priorities',
        lead: 'The priorities and statuses every ticket uses. They are built in and cannot be changed here yet.',
        legacy: ['priorities>#priorities', 'statuses>#statuses'],
        settings: [
          { anchor: 'priorities', label: 'Ticket priorities', keywords: 'priority critical high medium low urgency' },
          { anchor: 'statuses', label: 'Ticket statuses', keywords: 'status open in progress resolved closed colour' }
        ]
      },
      {
        id: 'custom-fields',
        label: 'Custom fields',
        icon: 'custom-fields',
        lead: 'Extra questions a ticket form asks, per category.',
        legacy: ['custom-fields'],
        settings: [
          { anchor: null, label: 'Custom fields on the ticket form', keywords: 'field question form extra required options' }
        ]
      },
      {
        id: 'sla',
        label: 'SLA rules',
        icon: 'sla-rules',
        lead: 'How quickly each category and priority must be answered and resolved, and what happens when it is not.',
        legacy: ['sla-rules'],
        settings: [
          { anchor: null, label: 'Response and resolution targets', keywords: 'sla service level target hours escalation breach' }
        ]
      },
      {
        id: 'sla-hours',
        label: 'SLA working hours',
        icon: 'sla-rules',
        lead: 'Which hours an SLA counts - office hours, 24/7 or a client’s contract - and which tickets get which.',
        legacy: [],
        settings: [
          { anchor: 'profiles', label: 'SLA profiles: working hours, holidays and closed days', keywords: 'business hours office hours 24/7 working week holidays closed days malta timezone profile' },
          { anchor: 'rules', label: 'Which tickets get which SLA profile', keywords: 'sla profile rule category company client contract' },
          { anchor: 'reapply', label: 'Re-apply SLA profiles to open tickets', keywords: 'reapply re-apply open tickets deadlines move' }
        ]
      },
      {
        id: 'templates',
        label: 'Ticket templates',
        icon: 'templates',
        lead: 'Ready-made tickets. They live in Ticket Mgmt, where they are used.',
        href: '/manage/templates',
        legacy: [],
        settings: [
          { anchor: null, label: 'Ticket templates (in Ticket Mgmt)', keywords: 'template ready-made checklist password reset' }
        ]
      },
      {
        // The monthly report (118) lives in Ticket Mgmt -> Reporting; its
        // email settings open from there (#email), so this is a door to them.
        id: 'monthly-report',
        label: 'Monthly report',
        icon: 'report',
        lead: 'The monthly report and its email. They live in Ticket Mgmt, under Reporting.',
        href: '/manage/reports#email',
        legacy: [],
        settings: [
          {
            anchor: null, label: 'Monthly report email: on or off, and who gets it (in Ticket Mgmt)',
            keywords: 'monthly report email summary pack recipients schedule first working day kpi management',
            key: 'reports_monthly_enabled',
            value: (c) => (has(c, 'reports_monthly_enabled') ? onOff(rule(c, 'reports_monthly_enabled') === true) : null)
          }
        ]
      },
      {
        id: 'automation',
        label: 'Automation',
        icon: 'automation',
        lead: 'Rules that run on their own: when something happens to a ticket or a device, do something about it.',
        legacy: ['workflows', 'automation'],
        settings: [
          { anchor: null, label: 'Automation rules', keywords: 'workflow automation trigger rule action when then' }
        ]
      }
    ]
  },
  {
    id: 'communication',
    label: 'Chat, calls & meetings',
    pages: [
      {
        id: 'messaging',
        label: 'Messaging',
        icon: 'chat',
        lead: 'Who can start a conversation with whom.',
        legacy: ['rules#messaging'],
        settings: [
          { anchor: 'staff-to-staff', label: 'Staff can message any other staff', keywords: 'chat message colleagues directory', key: 'chat_staff_to_staff', value: (c) => onOff(rule(c, 'chat_staff_to_staff') === true) },
          {
            anchor: 'contractor-to-contractor', label: 'Contractors can message each other', keywords: 'chat message contractors directory', key: 'chat_contractor_to_contractor',
            value: (c) => onOff(rule(c, 'chat_contractor_to_contractor') === true || rule(c, 'chat_open') === true),
            privacy: 'exposes', why: 'Lets your contractors find each other in chat.'
          },
          {
            anchor: 'chat-open', label: 'Everyone can message everyone', keywords: 'chat open directory all', key: 'chat_open',
            value: (c) => onOff(rule(c, 'chat_open') === true),
            privacy: 'exposes', why: 'Gives every contractor a list of everybody at NCMB.'
          }
        ]
      },
      {
        id: 'groups',
        label: 'Chat groups',
        icon: 'groups',
        lead: 'Who can create a group conversation, and what contractors may do with them.',
        legacy: ['rules#groups'],
        settings: [
          { anchor: 'groups-who', label: 'Who can create a group', keywords: 'group chat create admins staff everyone', key: 'chat_groups_who', value: (c) => ({ admins: 'Administrators only', staff: 'Staff', everyone: 'Everyone' }[rule(c, 'chat_groups_who')] || 'Staff') },
          { anchor: 'groups-contractors', label: 'Contractors can create groups', keywords: 'group chat contractors', key: 'chat_groups_contractors', value: (c) => (rule(c, 'chat_group_rules_ready') === true ? onOff(rule(c, 'chat_groups_contractors') === true) : null) },
          { anchor: 'groups-contractors', label: 'Rules a contractor may use for a group’s members', keywords: 'group rule members department contractors', key: 'chat_group_rules_contractors' }
        ]
      },
      {
        id: 'calls',
        label: 'Calls',
        icon: 'call',
        lead: 'Whether a call has to start from a ticket.',
        legacy: ['rules#calls'],
        settings: [
          { anchor: 'calls-without-ticket', label: 'Calls may be made without a ticket', keywords: 'call phone ring general screen share', key: 'calls_without_ticket', value: (c) => onOff(rule(c, 'calls_without_ticket') === true) }
        ]
      },
      {
        id: 'recording',
        label: 'Recording & AI',
        icon: 'record',
        lead: 'Who can record calls and use Claude for summaries, how people are told, what recordings are transcribed in and how long they are kept.',
        legacy: ['rules#recording>#feature-recording', 'rules#retention>#retention', 'rules#language>#language'],
        settings: [
          {
            anchor: 'feature-recording', label: 'Call recording & transcription: who can use it', keywords: 'record recording transcript calls can be recorded record button', value: (c) => featureWord(c, 'recording'),
            privacy: 'records', why: 'Lets people record calls and meeting rooms; the recordings are transcribed.'
          },
          {
            anchor: 'feature-ai', label: 'AI summaries & minutes (Claude): who can use it', keywords: 'ai claude summary minutes to-dos', value: (c) => featureWord(c, 'ai'),
            privacy: 'exposes', why: 'Sends transcripts to Claude to write summaries, to-dos and minutes.'
          },
          {
            anchor: 'consent', label: 'Before recording: announce or ask everyone', keywords: 'consent banner accept decline announce ask', key: 'recording_consent',
            value: (c) => (rule(c, 'recording_consent') === 'ask' ? 'Ask everyone' : 'Announce'),
            privacy: 'records', why: 'Whether everybody on a call must accept before it is recorded.'
          },
          { anchor: 'language', label: 'Transcribe recordings in', keywords: 'language malti english transcription', key: 'recording_language', value: (c) => ({ mixed: 'Malti + English', auto: 'Detect', 'en-GB': 'English', 'mt-MT': 'Malti', 'it-IT': 'Italiano', 'de-DE': 'Deutsch', 'fr-FR': 'Français' }[rule(c, 'recording_language')] || (has(c, 'recording_language') ? 'Malti + English' : null)) },
          {
            anchor: 'retention', label: 'Keep recordings for', keywords: 'retention delete days expire keep recordings', key: 'recording_retention_days',
            value: (c) => (Number.isFinite(Number(rule(c, 'recording_retention_days'))) ? (Number(rule(c, 'recording_retention_days')) > 0 ? `${Number(rule(c, 'recording_retention_days'))} days` : 'For ever') : null),
            privacy: 'records', why: 'How long the audio of a recording is kept.'
          }
        ]
      },
      {
        id: 'conversation-review',
        label: 'Conversation review',
        icon: 'review',
        lead: 'Whether administrators may read other people’s conversations, from Records → Chat archive.',
        legacy: ['rules#review'],
        settings: [
          {
            anchor: 'admin-review', label: 'Administrators may read other people’s conversations', keywords: 'chat archive monitoring read messages review', key: 'chat_admin_review',
            value: (c) => onOff(rule(c, 'chat_admin_review') === true),
            privacy: 'records', why: 'Lets an administrator open any General or group conversation, with a reason, in the Chat archive.'
          }
        ]
      },
      {
        id: 'meetings',
        label: 'Meetings',
        icon: 'meetings',
        lead: 'Who can organise and join meetings.',
        legacy: [],
        settings: [
          { anchor: 'feature-meetings', label: 'Meetings: who can use it', keywords: 'meeting invite guest lobby minutes', value: (c) => featureWord(c, 'meetings') }
        ]
      },
      {
        // Announcements (125) are written and followed up under Advanced
        // -> Announcements, where they are worked; this is a door to them.
        id: 'announcements',
        label: 'Announcements',
        icon: 'announce',
        lead: 'Announcements to everyone, staff, contractors, a department or named people. They live under Advanced.',
        href: '/announcements',
        legacy: [],
        settings: [
          { anchor: null, label: 'Announcements: write one, banners, who has read it (in Advanced)', keywords: 'announcement announce news notice banner important info everyone acknowledge read reminder broadcast' }
        ]
      }
    ]
  },
  {
    id: 'assets',
    label: 'Assets & devices',
    pages: [
      {
        id: 'register',
        label: 'Register',
        icon: 'assets',
        lead: 'Whether the asset register is in use, and who sees it.',
        legacy: ['assets', 'assets&part=switches'],
        settings: [
          { anchor: 'assets-enabled', label: 'The asset register is in use', keywords: 'assets register devices equipment', key: 'assets_enabled', value: (c) => onOff(rule(c, 'assets_enabled') === true) },
          {
            anchor: 'any-device', label: 'A ticket may name somebody else’s device', keywords: 'ticket device search register holder', key: 'assets_ticket_any_device',
            value: (c) => onOff(rule(c, 'assets_ticket_any_device') !== false),
            privacy: 'exposes', why: 'Lets anybody raising a ticket search the whole register by tag, serial or holder.'
          },
          { anchor: 'my-equipment', label: 'Staff see My equipment', keywords: 'my equipment devices assigned', key: 'assets_my_equipment', value: (c) => (has(c, 'assets_my_equipment') ? onOff(rule(c, 'assets_my_equipment') === true) : null) },
          { anchor: 'purge', label: 'Start the register again', keywords: 'purge reset start again wipe empty delete all devices numbering clear' }
        ]
      },
      {
        id: 'companies',
        label: 'Companies',
        icon: 'companies',
        lead: 'Whose devices share the register: each company’s name, short code and colour.',
        legacy: ['assets&part=companies'],
        settings: [{ anchor: null, label: 'Companies in the register', keywords: 'company client code colour' }]
      },
      {
        id: 'places',
        label: 'Places',
        icon: 'places',
        lead: 'Where devices are: offices, floors and rooms.',
        legacy: ['assets&part=places'],
        settings: [{ anchor: null, label: 'Places (locations)', keywords: 'location office floor room building' }]
      },
      {
        id: 'device-types',
        label: 'Device types',
        icon: 'device-types',
        lead: 'Laptop, Monitor, Phone… and the tag prefix each one’s labels start with.',
        legacy: ['assets&part=categories'],
        settings: [{ anchor: null, label: 'Device types and tag prefixes', keywords: 'asset category type laptop monitor prefix' }]
      },
      {
        id: 'register-access',
        label: 'Register access',
        icon: 'access',
        lead: 'Who may look at or change which part of the register - a person or a contractor.',
        legacy: ['assets&part=permissions'],
        settings: [{ anchor: null, label: 'Register access (grants)', keywords: 'asset permissions grant read write look change' }]
      },
      {
        id: 'reminders-labels',
        label: 'Reminders & labels',
        icon: 'labels',
        lead: 'When to warn about warranties and replacements, and your own label size for printing.',
        legacy: [],
        settings: [
          { anchor: 'warranty', label: 'Warn about warranties and replacements', keywords: 'warranty replacement expiry ending soon days', key: 'assets_warranty_notice_days', value: (c) => (has(c, 'assets_warranty_notice_days') ? `${rule(c, 'assets_warranty_notice_days') ?? 30} days ahead` : null) },
          { anchor: 'label-size', label: 'Your own label size', keywords: 'label print sticker avery brother dymo size', key: 'assets_label_custom', value: (c) => (has(c, 'assets_label_custom') ? (rule(c, 'assets_label_custom') ? 'Set' : 'Not set') : null) }
        ]
      },
      {
        id: 'device-agent',
        label: 'Device agent',
        icon: 'agent',
        lead: 'What the Jarvis agent on the firm’s computers reports, and what IT may ask it for.',
        legacy: ['assets&part=devices'],
        settings: [
          {
            anchor: 'heartbeat', label: 'Accept reports from the device agent', keywords: 'agent heartbeat monitoring report laptop', key: 'assets_heartbeat_enabled',
            value: (c) => onOff(rule(c, 'assets_heartbeat_enabled') === true),
            privacy: 'records', why: 'Enrolled computers report their serial, OS, encryption and disks once a day.'
          },
          {
            // Tied to the heartbeat switch: no setting of its own. Here so
            // Privacy & monitoring names it, as it names every other thing
            // the report carries about a person.
            anchor: 'heartbeat-signed-in', label: 'Who is signed in to each device', keywords: 'signed in logged in user account upn recent sign-ins fourteen days holder', key: 'assets_heartbeat_enabled',
            value: (c) => onOff(rule(c, 'assets_heartbeat_enabled') === true),
            privacy: 'records', why: 'The daily report says who is signed in to the machine and who signed in over the last fourteen days, by account and address (agent 0.5.0).'
          },
          {
            anchor: 'heartbeat-location', label: 'Device location', keywords: 'agent location office network coarse', key: 'assets_heartbeat_location',
            value: (c) => (rule(c, 'assets_heartbeat_location') === 'coarse' ? 'Coarse' : 'Off'),
            privacy: 'records', why: 'Which office network a device is on, or “away from the office”.'
          },
          { anchor: 'offline-days', label: 'Not reporting after', keywords: 'offline stale days agent', key: 'assets_offline_days', value: (c) => (has(c, 'assets_offline_days') ? `${rule(c, 'assets_offline_days')} days` : null) },
          {
            anchor: 'agent-auto-update', label: 'Agents update themselves', keywords: 'agent update upgrade version release self-update binary', key: 'agent_auto_update',
            value: (c) => onOff(rule(c, 'agent_auto_update') === true)
          },
          {
            anchor: 'diagnostics', label: 'Allow diagnostics on request', keywords: 'diagnostics checks uptime updates processor', key: 'assets_diagnostics_enabled',
            value: (c) => onOff(rule(c, 'assets_diagnostics_enabled') === true),
            privacy: 'records', why: 'IT can ask a computer to run read-only checks; the person at it sees who asked.'
          },
          {
            anchor: 'heartbeat-attached', label: 'List what is plugged into each device', keywords: 'attached usb monitor dock printer drive peripherals discovery', key: 'assets_heartbeat_attached',
            value: (c) => onOff(rule(c, 'assets_heartbeat_enabled') === true && rule(c, 'assets_heartbeat_attached') === true),
            privacy: 'records', why: 'The daily report lists the screens, docks, printers, drives and USB devices attached to each machine.'
          },
          {
            anchor: 'attached-autolink', label: 'Link plugged-in devices to the computer at once', keywords: 'attached autolink plugged in link register inbox screen dock keyboard mouse', key: 'assets_attached_autolink',
            value: (c) => onOff(rule(c, 'assets_heartbeat_enabled') === true && rule(c, 'assets_heartbeat_attached') === true && rule(c, 'assets_attached_autolink') !== false),
            privacy: 'records', why: 'A screen, dock, keyboard, mouse, webcam or headset a computer reports is added to the register as its device straight away, with the computer’s holder and place. Bluetooth things, USB sticks and network printers wait in the Discovery inbox instead; a thing a person has marked personal is never linked.'
          },
          {
            anchor: 'attached-autolink-office-only', label: 'Only when the computer is in an office', keywords: 'attached autolink office only home client seen where inbox wait', key: 'assets_attached_autolink_office_only',
            value: (c) => onOff(rule(c, 'assets_heartbeat_enabled') === true && rule(c, 'assets_heartbeat_attached') === true && rule(c, 'assets_attached_autolink') !== false && rule(c, 'assets_attached_autolink_office_only') !== false),
            privacy: 'records', why: 'A thing seen while the computer is at home or at a client waits in the Discovery inbox, marked with where it was seen, rather than becoming the firm’s device at once.'
          },
          {
            // 108: tied to the plugged-in list, with no switch of its own -
            // here so Privacy & monitoring names it, as it names everything
            // else the report is used to record about a person.
            anchor: 'desk-sittings', label: 'Who sat at which desk', keywords: 'desk workstation hot desk sitting seat where sat laptop dock monitor', key: 'assets_heartbeat_attached',
            value: (c) => onOff(rule(c, 'assets_heartbeat_enabled') === true && rule(c, 'assets_heartbeat_attached') === true),
            privacy: 'records', why: 'When a computer reports a desk’s screen or dock, Jarvis records that its holder sat at that desk, and from when to when. Seen by IT administrators only, on the desk’s page; kept 90 days.'
          },
          {
            anchor: 'network-scan', label: 'Allow a scan of the office network', keywords: 'network scan subnet discovery printers switches access points inbox', key: 'assets_network_scan',
            value: (c) => onOff(rule(c, 'assets_heartbeat_enabled') === true && rule(c, 'assets_diagnostics_enabled') === true && rule(c, 'assets_network_scan') === true),
            privacy: 'records', why: 'IT can ask an office PC to try every address on its subnet; every device on the office Wi-Fi, staff phones included, is seen.'
          },
          {
            anchor: 'maker-registry-auto', label: 'Keep the MAC maker registry up to date by itself', keywords: 'mac maker registry ieee oui vendor refresh discovery unknown', key: 'assets_oui_auto_refresh',
            value: (c) => onOff(rule(c, 'assets_oui_auto_refresh') === true),
            why: 'The server downloads the IEEE registry of MAC address blocks again once the copy in use is over 30 days old, so Discovery can name the makers of newer devices.'
          },
          {
            anchor: 'diagnostics-contractor', label: 'Let the contractor on a ticket ask for diagnostics too', keywords: 'diagnostics contractor', key: 'assets_diagnostics_contractor',
            value: (c) => onOff(rule(c, 'assets_diagnostics_enabled') === true && rule(c, 'assets_diagnostics_contractor') !== false),
            privacy: 'records', why: 'A contractor fixing a ticket about a device may run the reduced set of checks.'
          }
        ]
      },
      {
        id: 'enrolment',
        label: 'Enrolment',
        icon: 'enrol',
        lead: 'Machines waiting for approval, enrolment tokens and installer downloads, the known networks, and where the Discovery inbox and floor plans are.',
        legacy: [],
        settings: [
          { anchor: 'waiting', label: 'Waiting for your approval', keywords: 'enrolment reinstall approve reject queue' },
          { anchor: 'tokens', label: 'Enrolment tokens', keywords: 'token intune install agent' },
          { anchor: 'tokens', label: 'Installer downloads, last 30 days', keywords: 'download installer self-install' },
          { anchor: 'devices', label: 'Devices reporting (Asset Portal → Devices)', keywords: 'devices reporting list agent signed in holder stale not reporting encryption disk' },
          { anchor: 'networks', label: 'Known networks', keywords: 'wifi ssid public ip office network client site subnet gateway mac scan' },
          { anchor: 'discovery', label: 'Discovery inbox and floor plans', keywords: 'discovery inbox printers switches access points floor plan map pins devices without a heartbeat' }
        ]
      },
      {
        id: 'instructions',
        label: 'Instructions',
        icon: 'instructions',
        lead: 'Step-by-step instructions IT sends to people, and when to tell the sender nobody has done them.',
        legacy: ['assets&part=instructions'],
        settings: [
          { anchor: null, label: 'Instruction templates', keywords: 'instruction steps windows mac guide' },
          { anchor: 'overdue', label: 'Tell the sender after', keywords: 'instruction overdue days reminder', key: 'assets_instructions_overdue_days', value: (c) => (has(c, 'assets_instructions_overdue_days') ? `${rule(c, 'assets_instructions_overdue_days')} days` : null) }
        ]
      }
    ]
  },
  {
    id: 'content',
    label: 'Apps & content',
    pages: [
      {
        id: 'links',
        label: 'Links',
        icon: 'links',
        lead: 'Who can use Links. The shelf itself is edited in place, on the Links page.',
        legacy: [],
        settings: [
          { anchor: 'feature-links', label: 'Links: who can use it', keywords: 'links shelf websites favourites', value: (c) => featureWord(c, 'links') }
        ]
      },
      {
        id: 'calendars',
        label: 'To do & calendars',
        icon: 'calendars',
        lead: 'Who can use To do, emails that become to-dos, private calendar links, and the public event calendars offered as layers on everybody’s to-do Calendar.',
        legacy: ['calendars'],
        settings: [
          { anchor: 'feature-todos', label: 'To do: who can use it', keywords: 'todo to-do tasks lists', value: (c) => featureWord(c, 'todos') },
          { anchor: 'todo-workload', label: 'Who can see the team workload', keywords: 'workload team assigned admins staff shared lists', key: 'todo_workload_audience', value: (c) => (rule(c, 'todo_workload_audience') === 'staff_shared' ? 'Staff on shared lists' : 'Admins only') },
          { anchor: 'todo-email', label: 'Email to to-do', keywords: 'email forward inbox jarvis@todo.ncmb.ai resend receiving mail to-do', key: 'todo_email_enabled', value: (c) => (has(c, 'todo_email_enabled') ? onOff(rule(c, 'todo_email_enabled')) : null),
            privacy: 'records', why: 'Emails sent to jarvis@todo.ncmb.ai are stored as to-dos with their attachments; unknown senders are logged by address only.' },
          { anchor: 'todo-email-contractors', label: 'Contractors may email to-dos in', keywords: 'email contractor to-do inbox', key: 'todo_email_contractors', value: (c) => (has(c, 'todo_email_contractors') ? onOff(rule(c, 'todo_email_contractors')) : null) },
          { anchor: 'todo-email-auth', label: 'Only verified senders (DMARC)', keywords: 'email dmarc dkim spf spoof forged sender verified authentication refused', key: 'todo_email_require_auth', value: (c) => (has(c, 'todo_email_require_auth') ? onOff(rule(c, 'todo_email_require_auth')) : null) },
          { anchor: 'todo-email-arrives', label: 'When an email arrives', keywords: 'email sort wait suggest ai claude direct to-do straight away', key: 'todo_email_mode',
            value: (c) => (has(c, 'todo_email_mode') ? (rule(c, 'todo_email_mode') === 'direct' ? 'Becomes a to-do straight away' : rule(c, 'todo_email_ai') === false ? 'Waits to be sorted' : 'Waits to be sorted, with AI suggestions') : null),
            privacy: 'exposes', why: 'The text of an email sent to jarvis@todo.ncmb.ai may be sent to Claude to suggest to-dos; off when AI is off.' },
          { anchor: 'todo-email-log', label: 'Last 20 emails received', keywords: 'email log received outcome unknown sender' },
          { anchor: 'calendar-links', label: 'Private calendar links', keywords: 'calendar link subscribe ics outlook apple google ical feed private', key: 'calendar_links_enabled', value: (c) => (has(c, 'calendar_links_enabled') ? onOff(rule(c, 'calendar_links_enabled')) : null),
            privacy: 'exposes', why: 'Lets each person make a private address that shows their own to-dos and meetings to whatever calendar app holds it.' },
          { anchor: 'feeds', label: 'Event calendars (iCalendar feeds)', keywords: 'calendar feed ical holidays malta gozo culture' }
        ]
      },
      {
        id: 'documentation',
        label: 'Documentation',
        icon: 'documentation',
        lead: 'Who sees which runbooks, how-tos and workflows.',
        legacy: ['documentation'],
        subs: [
          { id: 'overview', label: 'Overview' },
          { id: 'default-access', label: 'Default access' },
          { id: 'groups', label: 'Groups' },
          { id: 'access-report', label: 'Access report' },
          { id: 'archive', label: 'Archive' },
          { id: 'activity', label: 'Activity' }
        ],
        settings: [
          { anchor: 'feature-docs', label: 'Documentation: who can open it', keywords: 'docs documentation runbooks feature', value: (c) => featureWord(c, 'docs') },
          { anchor: 'feature-help', label: 'Help: who can read it', keywords: 'help pages articles staff feature', value: (c) => featureWord(c, 'help') },
          { sub: 'default-access', anchor: null, label: 'Documentation: default access', keywords: 'docs permissions firm-wide inherit' },
          { sub: 'groups', anchor: null, label: 'Documentation: groups', keywords: 'docs groups contractors staff members rule' },
          { sub: 'access-report', anchor: null, label: 'Documentation: access report', keywords: 'docs who can see report person' },
          { sub: 'archive', anchor: null, label: 'Documentation: archive', keywords: 'docs archived restore' },
          { sub: 'activity', anchor: null, label: 'Documentation: activity', keywords: 'docs audit activity publish' }
        ]
      }
    ]
  },
  {
    id: 'system',
    label: 'Firm & system',
    pages: [
      {
        id: 'overview',
        label: 'Overview',
        icon: 'overview',
        lead: 'What still needs setting up on the server, what was changed last, and the pages people use most.',
        legacy: [],
        settings: [
          { anchor: 'setup', label: 'Setup status (migrations still to run)', keywords: 'migrate migration setup server database' },
          { anchor: 'recent', label: 'Last setting changes', keywords: 'recent changed history who' },
          { anchor: 'errors', label: 'Recent server errors (last 24 h)', keywords: 'errors log 500 crash failed request id ops degraded client' },
          { anchor: 'backups', label: 'Backups', keywords: 'backup pg_dump restore r2 railway dump copy database daily' },
          { anchor: 'jobs', label: 'Background jobs', keywords: 'jobs sweep clock timer sla digest transcription rates registry calendar last ran next due' }
        ]
      },
      {
        id: 'privacy',
        label: 'Privacy & monitoring',
        icon: 'privacy',
        lead: 'Every switch that records people or shows them to others, with its current value. Change each where it lives.',
        legacy: [],
        settings: [
          { anchor: null, label: 'Privacy & monitoring (GDPR)', keywords: 'gdpr privacy monitoring data protection staff notice' }
        ]
      },
      {
        // GDPR tools (122): a subject access export, and erasure by
        // anonymisation - administrators, behind the 'gdpr' switch.
        id: 'personal-data',
        label: 'Personal data requests',
        icon: 'data-request',
        lead: 'Answer a subject access request with an export of everything Jarvis holds about a person, or erase somebody who has left.',
        legacy: [],
        settings: [
          { anchor: 'export', label: 'Subject access export (download a person’s data)', keywords: 'gdpr sar subject access request export download zip personal data copy article 15' },
          { anchor: 'erase', label: 'Erase a person (anonymise)', keywords: 'gdpr erasure erase forget right to be forgotten anonymise delete leaver article 17' },
          { anchor: 'history', label: 'Erasures done', keywords: 'gdpr history erasures erased former staff member pseudonym log' }
        ]
      },
      {
        id: 'retention',
        label: 'Retention',
        icon: 'retention',
        lead: 'Every rule that deletes or anonymises personal data after a while: the ones that already run, and new ones to switch on.',
        legacy: [],
        settings: [
          { anchor: 'rules', label: 'Retention rules (transcripts and minutes, chat versions, emails, guests, sign-ins)', keywords: 'gdpr retention delete purge keep days months transcripts summaries minutes permanently chat versions emails guests sign-ins sign ins log' },
          { anchor: 'existing', label: 'What is already deleted, and when', keywords: 'retention recordings heartbeats location desk sittings backups errors' },
          { anchor: 'notice', label: 'Staff privacy notice', keywords: 'gdpr staff notice privacy notice employees informed monitoring' }
        ]
      },
      {
        id: 'processing',
        label: 'Record of processing',
        icon: 'register',
        lead: 'The register of what personal data Jarvis processes, why, on what lawful basis, who receives it and for how long.',
        legacy: [],
        settings: [
          { anchor: 'activities', label: 'Processing activities', keywords: 'gdpr ropa record of processing article 30 purposes lawful basis register' },
          { anchor: 'processors', label: 'Processors (Azure Speech, Anthropic, Resend, R2, Railway, Entra)', keywords: 'gdpr processors sub-processors dpa azure anthropic claude resend cloudflare r2 railway entra microsoft transfers' },
          { anchor: 'holds', label: 'What Jarvis holds about people', keywords: 'gdpr personal data inventory categories tables' },
          { anchor: 'record', label: 'Data-protection record', keywords: 'gdpr log audit record exports erasures retention' }
        ]
      },
      {
        id: 'records',
        label: 'Records',
        icon: 'records',
        lead: 'Where to look something up: the audit log, the chat archive and call recordings.',
        legacy: [],
        settings: [
          { anchor: null, label: 'Audit log, chat archive and call recordings', keywords: 'audit log archive recordings records history' }
        ]
      }
    ]
  }
];

/* ------------------------------------------------------------- lookups */

export const DEFAULT_PATH = '/settings/system/overview';

export const ALL_PAGES = GROUPS.flatMap((g) => g.pages.map((p) => ({ ...p, group: g.id, groupLabel: g.label })));

export function findPage(groupId, pageId) {
  return ALL_PAGES.find((p) => p.group === groupId && p.id === pageId) || null;
}

/** The address of a page, a sub-page and an anchor. */
export function pathOf(page, { sub = null, anchor = null, query = null } = {}) {
  if (page.href) return page.href;
  const subId = sub || (page.subs ? page.subs[0].id : null);
  return `/settings/${page.group}/${page.id}${subId ? `/${subId}` : ''}${query ? `?${query}` : ''}${anchor ? `#${anchor}` : ''}`;
}

/* ----------------------------------------------------------- redirects */

/**
 * Every old address, built from the pages' `legacy` lists:
 *   [{ tab, hash, part, to }]  e.g. { tab: 'rules', hash: 'retention',
 *   to: '/settings/communication/recording#retention' }.
 */
export const LEGACY = ALL_PAGES.flatMap((p) => (p.legacy || []).map((spec) => {
  const [from, where = ''] = spec.split('>');
  const [tabAndPart, hash = null] = from.split('#');
  const [tab, partBit] = tabAndPart.split('&');
  const part = partBit ? partBit.replace(/^part=/, '') : null;
  const anchor = where.startsWith('#') ? where.slice(1) : null;
  const query = where.startsWith('?') ? where.slice(1) : null;
  return { tab, hash, part, to: pathOf(p, { anchor, query }), notice: tab === 'rules' && !hash ? 'rules' : null };
}));

/**
 * Where an old /settings?tab=… address goes. `search` and `hash` are
 * location.search and location.hash. Null when there is no ?tab= at all;
 * the Overview for a tab nobody knows.
 */
export function legacyTarget(search, hash) {
  let tab = null;
  let part = null;
  try {
    const q = new URLSearchParams(search || '');
    tab = q.get('tab');
    part = q.get('part');
  } catch { return null; }
  if (!tab) return null;
  const h = String(hash || '').replace(/^#/, '') || null;
  const same = (e) => e.tab === tab;
  const hit = (h && LEGACY.find((e) => same(e) && e.hash === h))
    || (part && LEGACY.find((e) => same(e) && e.part === part))
    || LEGACY.find((e) => same(e) && !e.hash && !e.part);
  if (!hit) return { to: DEFAULT_PATH, notice: 'unknown' };
  return { to: hit.to, notice: hit.notice };
}

/* --------------------------------------------------------------- search */

const fold = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/[’'`]/g, '').replace(/&/g, ' and ');

/**
 * Every individual setting (and every page) that matches, best first.
 * Each result: { key, page, setting, label, where, path, value }.
 */
export function searchSettings(query, ctx) {
  const words = fold(query).split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const out = [];
  for (const page of ALL_PAGES) {
    const entries = [{ label: page.label, keywords: `${page.groupLabel} ${page.lead || ''}`, anchor: null, pageLevel: true },
      ...(page.settings || [])];
    entries.forEach((s, i) => {
      const label = fold(s.label);
      const hay = `${label} ${fold(s.keywords)} ${fold(page.label)} ${fold(page.groupLabel)}`;
      if (!words.every((w) => hay.includes(w))) return;
      let score = 0;
      const q = words.join(' ');
      if (label === q) score += 100;
      if (label.startsWith(q)) score += 50;
      if (label.includes(q)) score += 30;
      score += words.filter((w) => label.includes(w)).length * 10;
      if (s.pageLevel) score += 5;
      let value = null;
      try { value = s.value ? s.value(ctx) : null; } catch { value = null; }
      out.push({
        key: `${page.group}/${page.id}/${s.sub || ''}/${s.anchor || ''}/${i}`,
        page,
        setting: s,
        label: s.label,
        where: s.pageLevel ? page.groupLabel : `${page.groupLabel} › ${page.label}`,
        path: pathOf(page, { sub: s.sub || null, anchor: s.anchor || null }),
        external: Boolean(page.href),
        value,
        score
      });
    });
  }
  out.sort((a, b) => b.score - a.score || a.label.localeCompare(b.label));
  const seen = new Set();
  return out.filter((r) => {
    const k = `${r.path}|${r.label}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  }).slice(0, 12);
}

/* ------------------------------------------------- privacy & monitoring */

/** Every setting flagged as recording or exposing people, with its page. */
export function privacyRows() {
  return ALL_PAGES.flatMap((page) => (page.settings || [])
    .filter((s) => s.privacy)
    .map((s) => ({ page, setting: s, path: pathOf(page, { anchor: s.anchor }) })));
}

/* -------------------------------------------- app_settings key → label */

/** The setting an app_settings key belongs to, for "last changed". */
export function settingOfKey(key) {
  for (const page of ALL_PAGES) {
    const s = (page.settings || []).find((x) => x.key === key);
    if (s) return { page, setting: s, path: pathOf(page, { anchor: s.anchor }) };
  }
  return null;
}
